/**
 * Klaviyo Back In Stock Notification System
 * Handles out-of-stock product notifications via Klaviyo
 */

const KLAVIYO_CONFIG = {
  publicKey: 'MTDCz7',
  backInStockListId: 'XEpu3k',
  marketingListId: 'MbLjx8',
  apiUrl: 'https://a.klaviyo.com/onsite/identify'
};

/**
 * Cleans variant title by removing "Default Title" if present
 * @param {string} title - The variant title to clean
 * @returns {string} - The cleaned variant title, or empty string if it was "Default Title"
 */
function cleanVariantTitle(title) {
  if (!title) return '';
  const cleaned = title.trim();
  // Remove "Default Title" case-insensitively
  if (cleaned.toLowerCase() === 'default title') {
    return '';
  }
  return cleaned;
}

class BackInStockKlaviyo {
  constructor() {
    this.modal = document.getElementById('BackInStockModal');
    this.notifyBtn = document.getElementById('notify-when-available-btn');
    this.form = document.getElementById('back-in-stock-form');
    this.currentVariant = null;
    this.productData = null;
    
    if (!this.modal) {
      console.warn('Back in stock modal not found');
      return;
    }
    
    // Product page notify button is optional - collection pages don't have it
    if (!this.notifyBtn) {
      console.warn('Back in stock notify button not found - likely on collection page');
    }
    
    this.init();
  }

  init() {
    // Listen for variant changes (product page only)
    if (this.notifyBtn) {
      this.setupVariantListener();
      
      // Handle notify button click
      this.notifyBtn.addEventListener('click', this.handleNotifyClick.bind(this));
    }
    
    // Handle modal close (works for both product and collection pages)
    const closeBtn = document.getElementById('ModalClose-BackInStockModal');
    if (closeBtn) {
      closeBtn.addEventListener('click', this.closeModal.bind(this));
    }
    
    // Handle form submission (works for both product and collection pages)
    if (this.form) {
      this.form.addEventListener('submit', this.handleFormSubmit.bind(this));
    }
    
    // Set up email input listener to enable/disable submit button
    const emailInput = document.getElementById('bis-email');
    if (emailInput) {
      emailInput.addEventListener('input', this.handleEmailInput.bind(this));
    }
    
    // Close modal on backdrop click
    if (this.modal) {
      this.modal.addEventListener('click', (e) => {
        if (e.target === this.modal) {
          this.closeModal();
        }
      });
    }
  }
  
  handleEmailInput() {
    this.refreshSubmitState();
  }

  /**
   * Reads the main variant picker (never the sticky one) and reports which value
   * the shopper has actually chosen at each option position.
   *
   * Liquid only pre-checks a radio when product.selected_variant is set, i.e. the
   * URL carries ?variant=. On a normal page load nothing is checked, so every
   * multi-value option counts as unchosen. An option rendering a single radio -
   * such as the Length a combined listing injects into a child product - is
   * treated as implicitly chosen, because there is nothing to pick.
   *
   * @returns {{complete: boolean, chosen: Array<string|null>}|null} null when the
   *          page has no radio picker at all.
   */
  getVariantPickerState() {
    const picker = document.querySelector('variant-radios:not(.sticky_variant_radios)');
    if (!picker) return null;

    const chosen = [];
    let complete = true;

    picker.querySelectorAll('fieldset').forEach((fieldset) => {
      const radios = fieldset.querySelectorAll('input[type="radio"]');
      if (!radios.length) return;

      const checked = fieldset.querySelector('input[type="radio"]:checked');
      if (checked) {
        chosen.push(checked.value);
      } else if (radios.length === 1) {
        chosen.push(radios[0].value);
      } else {
        chosen.push(null);
        complete = false;
      }
    });

    return { complete, chosen };
  }

  hasCompleteVariantSelection() {
    const state = this.getVariantPickerState();
    // No picker means a single-variant product: nothing for the shopper to choose.
    return state ? state.complete : true;
  }

  loadProductData() {
    if (this.productData) return this.productData;
    const script = document.getElementById('ProductData');
    if (!script) return null;
    try {
      this.productData = JSON.parse(script.textContent);
    } catch (error) {
      console.error('Error parsing product data:', error);
      return null;
    }
    return this.productData;
  }

  /**
   * Shows the modal size dropdown, listing only out-of-stock variants still
   * reachable given whatever the shopper HAS chosen. Used on the product page
   * when notify was pressed without a full selection, so nobody is subscribed to
   * a size they never picked.
   *
   * @returns {boolean} true when a chooser is on screen awaiting input.
   */
  showPdpVariantChooser(state) {
    const data = this.loadProductData();
    const wrapper = this.modal.querySelector('.back-in-stock-modal__variant-select');
    const select = this.modal.querySelector('#bis-variant-select');
    if (!data || !data.variants || !wrapper || !select) return false;

    const candidates = data.variants.filter((variant) => {
      if (variant.available) return false;
      const options = variant.options || [];
      return state.chosen.every((value, index) => value === null || options[index] === value);
    });

    if (!candidates.length) return false;

    select.innerHTML = '<option value="">Select size...</option>';
    candidates.forEach((variant) => {
      const option = document.createElement('option');
      option.value = variant.id;
      option.textContent = cleanVariantTitle(variant.title || '');
      select.appendChild(option);
    });

    select.onchange = (event) => {
      const picked = candidates.find((variant) => String(variant.id) === String(event.target.value));
      const variantTitleEl = document.getElementById('bis-modal-variant-title');
      const variantIdEl = document.getElementById('bis-variant-id');

      if (picked) {
        this.modal.dataset.selectedVariantId = picked.id;
        this.modal.dataset.selectedVariantTitle = cleanVariantTitle(picked.title || '');
        // #ProductData prices are in cents; carried so the Klaviyo event keeps a
        // real Price instead of the collection branch's hard-coded 0.
        if (picked.price != null) this.modal.dataset.selectedVariantPrice = picked.price;
        if (variantIdEl) variantIdEl.value = picked.id;
        if (variantTitleEl) variantTitleEl.textContent = cleanVariantTitle(picked.title || '');
      } else {
        delete this.modal.dataset.selectedVariantId;
        delete this.modal.dataset.selectedVariantTitle;
        delete this.modal.dataset.selectedVariantPrice;
        if (variantIdEl) variantIdEl.value = '';
        if (variantTitleEl) variantTitleEl.textContent = '';
      }

      this.refreshSubmitState();
    };

    wrapper.style.display = 'block';
    return true;
  }

  /**
   * Submit stays disabled until there is a valid email and, whenever the size
   * chooser is showing, a chosen size.
   */
  refreshSubmitState() {
    const submitBtn = this.form?.querySelector('.back-in-stock-modal__submit');
    if (!submitBtn) return;

    const email = document.getElementById('bis-email')?.value.trim() || '';
    const wrapper = this.modal?.querySelector('.back-in-stock-modal__variant-select');
    const needsVariant = !!wrapper && wrapper.style.display !== 'none';
    const hasVariant = !needsVariant || !!this.modal?.dataset.selectedVariantId;
    const ready = !!email && this.isValidEmail(email) && hasVariant;

    submitBtn.disabled = !ready;
    submitBtn.style.opacity = ready ? '1' : '0.5';
  }

  setupVariantListener() {
    // Listen to the product form input changes (for size/color selection)
    const variantInputs = document.querySelectorAll('input[name*="option"], input[type="radio"][name*="Size"], input[type="radio"][name*="Color"]');
    variantInputs.forEach(input => {
      input.addEventListener('change', () => {
        // Small delay to ensure variant ID is updated
        setTimeout(() => {
          this.updateCurrentVariant();
        }, 50);
      });
    });

    // Also watch for the hidden variant ID input changes
    const variantIdInput = document.querySelector('input[name="id"]');
    if (variantIdInput) {
      // Use MutationObserver to detect when the value attribute changes
      const observer = new MutationObserver(() => {
        this.updateCurrentVariant();
      });
      observer.observe(variantIdInput, {
        attributes: true,
        attributeFilter: ['value']
      });
    }

    // Set initial variant
    this.updateCurrentVariant();
  }

  updateCurrentVariant() {
    try {
      // Get product data from script tag
      const productDataScript = document.getElementById('ProductData');
      if (!productDataScript) {
        console.warn('ProductData script tag not found');
        return;
      }
      
      if (!this.productData) {
        try {
          this.productData = JSON.parse(productDataScript.textContent);
        } catch (parseError) {
          console.error('Error parsing product data:', parseError);
          return;
        }
      }

      // Get currently selected variant
      const variantIdInput = document.querySelector('input[name="id"]');
      if (!variantIdInput) {
        console.warn('Variant ID input not found');
        // Try to use first variant as fallback
        if (this.productData && this.productData.variants && this.productData.variants.length > 0) {
          this.currentVariant = this.productData.variants[0];
        }
        return;
      }
      
      if (this.productData && this.productData.variants) {
        const variantId = parseInt(variantIdInput.value);
        this.currentVariant = this.productData.variants.find(v => v.id === variantId);
        
        if (this.currentVariant) {
          this.updateButtonVisibility(this.currentVariant);
        } else {
          console.warn('Variant not found in product data for ID:', variantId);
        }
      }
    } catch (error) {
      console.error('Error updating current variant:', error);
    }
  }

  updateButtonVisibility(variant) {
    if (!variant || !this.notifyBtn) return;
    
    this.currentVariant = variant;
    const addToCartBtn = document.querySelector('product-form button[type="submit"]');
    
    // Update button data attributes with current variant
    this.notifyBtn.setAttribute('data-variant-id', variant.id);
    this.notifyBtn.setAttribute('data-variant-title', cleanVariantTitle(variant.title || 'Default'));
    
    // Show notify button if variant is out of stock
    if (!variant.available) {
      this.notifyBtn.classList.remove('hidden');
      if (addToCartBtn) {
        addToCartBtn.classList.add('hidden');
      }
    } else {
      this.notifyBtn.classList.add('hidden');
      if (addToCartBtn) {
        addToCartBtn.classList.remove('hidden');
      }
    }
  }

  async handleNotifyClick() {
    // Never auto-subscribe to a variant the shopper has not chosen. The hidden
    // id input falls back to the first variant, so an untouched picker would
    // otherwise sign a logged-in shopper up for whatever size happens to be
    // first - with no modal and no confirmation.
    if (!this.hasCompleteVariantSelection()) {
      this.openModal();
      return;
    }

    const isLoggedIn = this.notifyBtn.getAttribute('data-is-logged-in') === 'true';
    const customerEmail = this.notifyBtn.getAttribute('data-customer-email');
    
    // Check if user is logged in to Shopify
    if (isLoggedIn && customerEmail) {
      // User is logged in - directly subscribe them
      await this.subscribeToBackInStock(customerEmail, false);
      this.showSuccessState();
      return;
    }
    
    // Check if user is already identified in Klaviyo (via previous opt-in)
    const klaviyoEmail = await this.getKlaviyoIdentifiedEmail();
    if (klaviyoEmail) {
      // User is identified in Klaviyo - directly subscribe them
      await this.subscribeToBackInStock(klaviyoEmail, false);
      this.showSuccessState();
      return;
    }
    
    // User is not logged in and not identified - show modal
    this.openModal();
  }
  
  async getKlaviyoIdentifiedEmail() {
    // Method 1: Check localStorage for Klaviyo identify data
    try {
      const klaviyoData = localStorage.getItem('klaviyo_identify');
      if (klaviyoData) {
        const parsed = JSON.parse(klaviyoData);
        if (parsed && parsed.$email) {
          return parsed.$email;
        }
      }
    } catch (error) {
      console.warn('Error reading Klaviyo data from localStorage:', error);
    }
    
    // Method 2: Check Klaviyo's internal state via cookies
    try {
      const cookies = document.cookie.split(';');
      for (let cookie of cookies) {
        const [name, value] = cookie.trim().split('=');
        if (name === '__kla_id') {
          // Decode the Klaviyo cookie to get email
          try {
            const decoded = decodeURIComponent(value);
            const emailMatch = decoded.match(/"email":"([^"]+)"/);
            if (emailMatch && emailMatch[1]) {
              return emailMatch[1];
            }
          } catch (e) {
            console.warn('Error decoding Klaviyo cookie:', e);
          }
        }
      }
    } catch (error) {
      console.warn('Error reading cookies:', error);
    }
    
    // Method 3: Check if _learnq has stored identification
    if (typeof _learnq !== 'undefined') {
      try {
        // Access the internal queue to check for identify calls
        for (let item of _learnq) {
          if (Array.isArray(item) && item[0] === 'identify' && item[1] && item[1].$email) {
            return item[1].$email;
          }
        }
      } catch (error) {
        console.warn('Error checking _learnq queue:', error);
      }
    }
    
    return null;
  }

  openModal() {
    if (!this.modal) {
      console.error('Modal not found');
      return;
    }

    const productImage = this.notifyBtn.getAttribute('data-product-image');
    const productTitle = this.notifyBtn.getAttribute('data-product-title');
    const productId = this.notifyBtn.getAttribute('data-product-id');

    // A size chosen on a previous open must not leak into this one. Escape-close
    // goes through ModalDialog.hide(), never closeModal(), so the dataset can
    // still hold the last open's pick.
    delete this.modal.dataset.selectedVariantId;
    delete this.modal.dataset.selectedVariantTitle;
    delete this.modal.dataset.selectedVariantPrice;

    // A shopper mid-selection must pick a size in the modal: the hidden id input
    // silently falls back to the first variant, so trusting it here would
    // prefill a size they never selected.
    const pickerState = this.getVariantPickerState();
    let needsChoice = !!pickerState && !pickerState.complete;

    if (needsChoice) {
      // subscribeToBackInStock reads product details from the dataset whenever a
      // variant is selected there, so populate them alongside the chooser.
      this.modal.dataset.productId = productId;
      this.modal.dataset.productTitle = productTitle;
      this.modal.dataset.productImage = productImage || '';

      // Templates that never render #ProductData (supplement PDPs) and selections
      // whose remaining variants are all in stock cannot offer a chooser - fall
      // back to the normal variant resolution below rather than dead-ending the
      // signup with no variant at all.
      if (!this.showPdpVariantChooser(pickerState)) {
        needsChoice = false;
      }
    }

    if (!needsChoice) {
      // Try to get variant from button data attributes first
      const variantIdFromButton = this.notifyBtn.getAttribute('data-variant-id');
      const variantTitleFromButton = this.notifyBtn.getAttribute('data-variant-title');

      // Check if values are not empty strings
      if (variantIdFromButton && variantIdFromButton.trim() !== '' &&
          variantTitleFromButton && variantTitleFromButton.trim() !== '') {
        // Use variant data directly from button (clean the title)
        this.currentVariant = {
          id: parseInt(variantIdFromButton),
          title: cleanVariantTitle(variantTitleFromButton)
        };
      } else {
        // Fallback to detection method
        console.warn('Variant data not on button (id:', variantIdFromButton, ', title:', variantTitleFromButton, '), attempting to detect...');

        if (!this.currentVariant) {
          this.updateCurrentVariant();

          // If still no variant after update, try to get first available variant or any variant
          if (!this.currentVariant && this.productData && this.productData.variants) {
            // Find first out-of-stock variant, or just use first variant
            const outOfStockVariant = this.productData.variants.find(v => !v.available);
            this.currentVariant = outOfStockVariant || this.productData.variants[0];
          }

          if (!this.currentVariant) {
            console.error('Cannot open modal: No variant could be detected');
            return;
          }
        }
      }

      // A chooser left visible by an Escape-closed open would keep gating submit
      // on a dataset pick that was just cleared - hide it for this complete open.
      const wrapper = this.modal.querySelector('.back-in-stock-modal__variant-select');
      if (wrapper) wrapper.style.display = 'none';
    }

    // Populate modal with product info
    document.getElementById('bis-modal-product-image').src = productImage;
    document.getElementById('bis-modal-product-image').alt = productTitle;
    document.querySelector('.back-in-stock-modal__title').textContent = productTitle;
    document.getElementById('bis-modal-variant-title').textContent =
      needsChoice ? '' : cleanVariantTitle(this.currentVariant.title || '');
    document.getElementById('bis-variant-id').value = needsChoice ? '' : this.currentVariant.id;
    document.getElementById('bis-product-id').value = productId;

    // Reset form
    this.form.reset();
    document.getElementById('bis-success-message').classList.add('hidden');
    document.getElementById('bis-error-message').classList.add('hidden');

    const description = document.getElementById('bis-modal-description');
    if (description) {
      description.textContent = needsChoice
        ? 'Select your size and enter your Email Address and we will notify you when the item becomes available.'
        : 'Enter your Email Address and we will notify you when the item becomes available.';
    }

    // Submit stays disabled until email, and size when asked for, are valid
    this.refreshSubmitState();

    // Open modal
    this.modal.setAttribute('open', '');
    document.body.style.overflow = 'hidden';
  }

  closeModal() {
    if (!this.modal) return;

    this.modal.removeAttribute('open');
    document.body.style.overflow = '';

    // Reset description to product page version
    const modalDescription = this.modal.querySelector('#bis-modal-description');
    if (modalDescription) {
      modalDescription.textContent = 'Enter your Email Address and we will notify you when the item becomes available.';
    }

    // Hide variant selector
    const variantSelectWrapper = this.modal.querySelector('.back-in-stock-modal__variant-select');
    if (variantSelectWrapper) {
      variantSelectWrapper.style.display = 'none';
    }

    // Clear collection page data from modal dataset
    delete this.modal.dataset.selectedVariantId;
    delete this.modal.dataset.selectedVariantTitle;
    delete this.modal.dataset.selectedVariantPrice;
    delete this.modal.dataset.productId;
    delete this.modal.dataset.productTitle;
    delete this.modal.dataset.productImage;
  }

  async handleFormSubmit(e) {
    e.preventDefault();
    
    const submitBtn = this.form?.querySelector('.back-in-stock-modal__submit');
    const emailInput = document.getElementById('bis-email');
    const marketingOptInInput = document.getElementById('bis-marketing-opt-in');
    const successMessage = document.getElementById('bis-success-message');
    const errorMessage = document.getElementById('bis-error-message');
    
    // Validate elements exist
    if (!emailInput || !marketingOptInInput || !submitBtn) {
      console.error('Required form elements not found');
      return;
    }
    
    const email = emailInput.value.trim();
    const marketingOptIn = marketingOptInInput.checked;

    // Size is asked for above the email field, so validate it first.
    const variantWrapper = this.modal?.querySelector('.back-in-stock-modal__variant-select');
    if (variantWrapper && variantWrapper.style.display !== 'none' && !this.modal.dataset.selectedVariantId) {
      const variantSelect = document.getElementById('bis-variant-select');
      if (variantSelect) {
        variantSelect.style.borderColor = '#ef4444';
        setTimeout(() => {
          variantSelect.style.borderColor = '';
        }, 2000);
      }
      return;
    }

    // Validate email
    if (!email || !this.isValidEmail(email)) {
      emailInput.style.borderColor = '#ef4444';
      setTimeout(() => {
        emailInput.style.borderColor = '';
      }, 2000);
      return;
    }
    
    // Hide previous messages
    successMessage?.classList.add('hidden');
    errorMessage?.classList.add('hidden');
    
    // Show loading state
    submitBtn.classList.add('loading');
    submitBtn.disabled = true;
    
    try {
      await this.subscribeToBackInStock(email, marketingOptIn);
      
      // Show success message
      successMessage?.classList.remove('hidden');
      
      // Close modal after 2 seconds
      setTimeout(() => {
        this.closeModal();
      }, 2000);
      
    } catch (error) {
      console.error('Error subscribing to back in stock:', error);
      errorMessage?.classList.remove('hidden');
    } finally {
      submitBtn.classList.remove('loading');
      submitBtn.disabled = false;
    }
  }

  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  async subscribeToBackInStock(email, marketingOptIn) {
    // Check if data is coming from collection page (modal dataset) or product page
    const modal = document.querySelector('#BackInStockModal');
    const fromCollectionPage = modal && modal.dataset.selectedVariantId;

    let variantId, variantTitle, productId, productTitle, productImage;

    if (fromCollectionPage) {
      // Collection page data from modal dataset
      variantId = modal.dataset.selectedVariantId;
      variantTitle = cleanVariantTitle(modal.dataset.selectedVariantTitle || '');
      productId = modal.dataset.productId;
      productTitle = modal.dataset.productTitle;
      productImage = modal.dataset.productImage;

      if (!variantId) {
        throw new Error('Please select a size');
      }
    } else {
      // Product page data from current variant and notify button
      if (!this.currentVariant) {
        throw new Error('No variant selected');
      }
      variantId = this.currentVariant.id;
      variantTitle = cleanVariantTitle(this.currentVariant.title || 'Default');
      productId = this.notifyBtn.getAttribute('data-product-id');
      productTitle = this.notifyBtn.getAttribute('data-product-title');
      productImage = this.notifyBtn.getAttribute('data-product-image');
    }
    
    try {
      // Step 1: Try Klaviyo's native back-in-stock subscription first
      // This will work if products are synced to Klaviyo's catalog
      const nativeBackInStockPayload = {
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            channels: ['EMAIL'],
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: email
                }
              }
            }
          },
          relationships: {
            variant: {
              data: {
                type: 'catalog-variant',
                id: `$shopify:::$default:::${variantId}`
              }
            }
          }
        }
      };

      let nativeBackInStockSuccess = false;
      
      try {
        const nativeResponse = await fetch(
          `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${KLAVIYO_CONFIG.publicKey}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'revision': '2024-10-15'
            },
            body: JSON.stringify(nativeBackInStockPayload)
          }
        );

        if (nativeResponse.ok) {
          const nativeText = await nativeResponse.text();
          nativeBackInStockSuccess = true;
        } else {
          const errorText = await nativeResponse.text();
          console.warn('Native back-in-stock subscription failed with status:', nativeResponse.status);
          console.warn('Error response:', errorText);
          console.warn('This may indicate the Shopify catalog is not synced to Klaviyo. Using fallback method.');
        }
      } catch (error) {
        console.warn('Native back-in-stock subscription failed, using fallback method:', error);
      }

      // Step 1b: Fallback - Subscribe to the back-in-stock list
      const backInStockListPayload = {
        data: {
          type: 'subscription',
          attributes: {
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: email
                }
              }
            }
          },
          relationships: {
            list: {
              data: {
                type: 'list',
                id: KLAVIYO_CONFIG.backInStockListId
              }
            }
          }
        }
      };

      const backInStockResponse = await fetch(
        `https://a.klaviyo.com/client/subscriptions/?company_id=${KLAVIYO_CONFIG.publicKey}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'revision': '2024-10-15'
          },
          body: JSON.stringify(backInStockListPayload)
        }
      );

      if (!backInStockResponse.ok) {
        const errorText = await backInStockResponse.text();
        console.error('Back in stock list subscription failed:', errorText);
        throw new Error(`Back in stock list subscription failed: ${backInStockResponse.status}`);
      }

      // 202 responses might have empty body
      const backInStockText = await backInStockResponse.text();
      const backInStockData = backInStockText ? JSON.parse(backInStockText) : {};

      // Step 2: Create/update profile with variant data
      const profilePayload = {
        data: {
          type: 'profile',
          attributes: {
            email: email,
            properties: {
              'Back In Stock Product ID': productId,
              'Back In Stock Product Title': productTitle,
              'Back In Stock Product Image': productImage,
              'Back In Stock Product URL': window.location.href,
              'Back In Stock Variant ID': variantId,
              'Back In Stock Variant Title': variantTitle,
              'Back In Stock Request Date': new Date().toISOString()
            }
          }
        }
      };

      const profileResponse = await fetch(
        `https://a.klaviyo.com/client/profiles/?company_id=${KLAVIYO_CONFIG.publicKey}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'revision': '2024-10-15'
          },
          body: JSON.stringify(profilePayload)
        }
      );

      if (profileResponse.ok) {
        const profileText = await profileResponse.text();
        const profileData = profileText ? JSON.parse(profileText) : {};
      }

      // Step 3: Track event for Klaviyo flow triggers
      const eventPayload = {
        data: {
          type: 'event',
          attributes: {
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: email
                }
              }
            },
            metric: {
              data: {
                type: 'metric',
                attributes: {
                  name: 'Subscribed to Back in Stock'
                }
              }
            },
            properties: {
              'Product ID': productId,
              'Product Title': productTitle,
              'Variant ID': variantId,
              'Variant Title': variantTitle,
              'Product URL': window.location.href,
              'Price': fromCollectionPage
                ? (Number(modal.dataset.selectedVariantPrice) / 100 || 0)
                : (this.currentVariant?.price / 100 || 0),
              'Currency': typeof Shopify !== 'undefined' ? Shopify.currency.active : 'USD',
              'Product Image': productImage
            }
          }
        }
      };

      const eventResponse = await fetch(
        `https://a.klaviyo.com/client/events/?company_id=${KLAVIYO_CONFIG.publicKey}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'revision': '2024-10-15'
          },
          body: JSON.stringify(eventPayload)
        }
      );

      if (eventResponse.ok) {
        const eventText = await eventResponse.text();
        const eventData = eventText ? JSON.parse(eventText) : {};
      }

      // Step 4: If marketing opt-in is checked, subscribe to marketing list
      if (marketingOptIn) {
        const marketingPayload = {
          data: {
            type: 'subscription',
            attributes: {
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email: email
                  }
                }
              }
            },
            relationships: {
              list: {
                data: {
                  type: 'list',
                  id: KLAVIYO_CONFIG.marketingListId
                }
              }
            }
          }
        };

        const marketingResponse = await fetch(
          `https://a.klaviyo.com/client/subscriptions/?company_id=${KLAVIYO_CONFIG.publicKey}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'revision': '2024-10-15'
            },
            body: JSON.stringify(marketingPayload)
          }
        );

        if (!marketingResponse.ok) {
          console.warn('Marketing list subscription failed (continuing anyway)');
        } else {
          const marketingText = await marketingResponse.text();
          const marketingData = marketingText ? JSON.parse(marketingText) : {};
        }
      }

      // Use _learnq for additional tracking
      if (typeof _learnq !== 'undefined') {
        _learnq.push(['identify', {
          '$email': email,
          '$source': 'Back in Stock Form'
        }]);
        
        _learnq.push(['track', 'Subscribed to Back in Stock', {
          'Product ID': productId,
          'Product Title': productTitle,
          'Variant ID': variantId,
          'Variant Title': variantTitle,
          'Product URL': window.location.href,
          'Price': fromCollectionPage
            ? (Number(modal.dataset.selectedVariantPrice) / 100 || 0)
            : (this.currentVariant?.price / 100 || 0),
          'Currency': typeof Shopify !== 'undefined' ? Shopify.currency.active : 'USD'
        }]);
      }
      
      return true;
      
    } catch (error) {
      console.error('Klaviyo subscription error:', error);
      throw error;
    }
  }


  showSuccessState() {
    // Change button to green success state
    this.notifyBtn.classList.add('notify-success');
    this.notifyBtn.querySelector('.notify-btn-text').textContent = 'Notification activated!';
    this.notifyBtn.style.backgroundColor = '#10b981';
    this.notifyBtn.style.borderColor = '#10b981';
    this.notifyBtn.disabled = true;
    
    // Reset after variant change
    setTimeout(() => {
      const variantInputs = document.querySelectorAll('input[name*="option"], input[name="id"]');
      variantInputs.forEach(input => {
        input.addEventListener('change', () => {
          this.resetButtonState();
        }, { once: true });
      });
    }, 100);
  }

  resetButtonState() {
    if (!this.notifyBtn) return;
    
    this.notifyBtn.classList.remove('notify-success');
    this.notifyBtn.querySelector('.notify-btn-text').textContent = 
      this.notifyBtn.getAttribute('data-original-text') || 'Notify me when available';
    this.notifyBtn.style.backgroundColor = '';
    this.notifyBtn.style.borderColor = '';
    this.notifyBtn.disabled = false;
  }
}

// Handle collection page "Notify when available" buttons
class CollectionBackInStock {
  constructor() {
    this.modal = document.querySelector('#BackInStockModal');
    if (!this.modal) {
      console.warn('Back in stock modal not found - skipping collection page handlers');
      return;
    }

    // Track buttons we've already attached listeners to (persist across reinit calls)
    this.attachedButtons = new WeakSet();
    
    // Bind the handler so we can remove it later if needed
    this.boundHandleClick = this.handleCollectionButtonClick.bind(this);

    this.initCollectionButtons();
  }
  
  // Public method to re-initialize buttons (useful for dynamic content)
  reinit() {
    this.initCollectionButtons();
  }

  initCollectionButtons() {
    // Helper function to attach listener to a button
    const attachListener = (button) => {
      // Skip if we've already attached a listener to this button
      if (this.attachedButtons.has(button)) {
        return;
      }
      button.addEventListener('click', this.boundHandleClick);
      this.attachedButtons.add(button);
    };
    
    // Find all collection page notify buttons
    // Try multiple selectors to catch buttons in different contexts
    const selectors = [
      '.card-product__oos-banner.bis-link',
      '.notify.bis-link',
      '.notify.link.bis-link',
      'button.notify.bis-link',
      'button[class*="notify"][class*="bis-link"]'
    ];
    
    let collectionButtons = [];
    for (const selector of selectors) {
      const found = document.querySelectorAll(selector);
      if (found.length > 0) {
        collectionButtons = Array.from(found);
        break;
      }
    }
    
    
    collectionButtons.forEach(button => {
      attachListener(button);
    });
    
    // Use MutationObserver to handle dynamically added buttons (e.g., on product pages with sections)
    // Only create observer if it doesn't already exist
    if (typeof MutationObserver !== 'undefined' && !this.buttonObserver) {
      // Store observer reference for potential cleanup
      this.buttonObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) { // Element node
              // Check if the added node is a button or contains buttons
              // Try multiple selectors
              let newButtons = [];
              const selectors = [
                '.card-product__oos-banner.bis-link',
                '.notify.bis-link',
                '.notify.link.bis-link',
                'button.notify.bis-link',
                'button[class*="notify"][class*="bis-link"]'
              ];
              
              for (const selector of selectors) {
                if (node.matches && node.matches(selector)) {
                  newButtons = [node];
                  break;
                } else if (node.querySelectorAll) {
                  const found = node.querySelectorAll(selector);
                  if (found.length > 0) {
                    newButtons = Array.from(found);
                    break;
                  }
                }
              }
              
              if (newButtons.length > 0) {
              }
              
              newButtons.forEach(button => {
                attachListener(button);
              });
            }
          });
        });
      });
      
      // Observe the entire document for changes
      this.buttonObserver.observe(document.body, {
        childList: true,
        subtree: true
      });
    }
  }

  handleCollectionButtonClick(event) {
    const button = event.currentTarget;
    const productId = button.dataset.productId;
    const productTitle = button.dataset.productTitle;
    const productImage = button.dataset.productImage;
    const productUrl = button.dataset.productUrl;
    const variantsBase64 = button.dataset.variants;

    // Validate variants data exists
    if (!variantsBase64 || variantsBase64.trim() === '') {
      console.error('Variants data is missing. The product data script may not have loaded yet.');
      return;
    }

    // Parse variants. Payloads may be UTF-8-safe base64 (btoa of UTF-8 bytes,
    // for titles with ™/é/…) or legacy plain btoa(JSON) — try UTF-8 first.
    let variants = [];
    try {
      const bin = atob(variantsBase64);
      let variantsJson;
      try {
        variantsJson = decodeURIComponent(escape(bin));
      } catch (e) {
        variantsJson = bin;
      }
      variants = JSON.parse(variantsJson);
    } catch (error) {
      console.error('[BIS] Error parsing variants:', error);
      return;
    }

    // Validate we have valid variants array
    if (!Array.isArray(variants) || variants.length === 0) {
      console.error('Invalid or empty variants array');
      return;
    }

    // Open modal
    this.openModal(productId, productTitle, productImage, productUrl, variants);
  }

  openModal(productId, productTitle, productImage, productUrl, variants) {
    if (!this.modal) {
      console.error('[BIS] Modal not found!');
      return;
    }
    // Populate modal fields
    const modalProductTitle = this.modal.querySelector('#BackInStockModal-Heading');
    const modalProductImage = this.modal.querySelector('#bis-modal-product-image');
    const modalDescription = this.modal.querySelector('#bis-modal-description');
    const variantSelect = this.modal.querySelector('#bis-variant-select');
    const variantSelectWrapper = this.modal.querySelector('.back-in-stock-modal__variant-select');

    if (modalProductTitle) {
      modalProductTitle.textContent = productTitle;
    }

    // Determine the image to use - try multiple fallbacks
    let imageToUse = productImage;
    
    // Fallback 1: If productImage is empty, try to get it from the product card on the page
    if (!imageToUse || imageToUse.trim() === '') {
      const productCard = document.querySelector(`[data-product-type] [id*="${productId}"]`);
      if (productCard) {
        const cardImage = productCard.querySelector('.product-image-primary, img[data-src], img[src]');
        if (cardImage) {
          imageToUse = cardImage.src || cardImage.getAttribute('data-src') || cardImage.getAttribute('src');
        }
      }
    }
    
    // Fallback 2: Try to get image from variants if available
    if ((!imageToUse || imageToUse.trim() === '') && variants && variants.length > 0) {
      const firstVariant = variants[0];
      if (firstVariant && firstVariant.featured_image) {
        imageToUse = firstVariant.featured_image.src;
      }
    }
    
    // Set the image in the modal
    if (modalProductImage) {
      if (imageToUse && imageToUse.trim() !== '') {
        modalProductImage.src = imageToUse;
        modalProductImage.alt = productTitle;
        modalProductImage.style.display = '';
      } else {
        // Hide image if we can't find one
        modalProductImage.style.display = 'none';
        console.warn('No product image found for product:', productId);
      }
    }

    // Update description for collection page
    if (modalDescription) {
      modalDescription.textContent = 'Select your size and enter your Email Address and we will notify you when the item becomes available.';
    }

    // A pick left over from a previous open must not leak into this one -
    // Escape-close goes through ModalDialog.hide(), never the closeModal cleanup,
    // so without this a stale variant id could subscribe the shopper to another
    // product's variant.
    delete this.modal.dataset.selectedVariantId;
    delete this.modal.dataset.selectedVariantTitle;
    delete this.modal.dataset.selectedVariantPrice;

    // Store product data on modal for form submission (use resolved image with fallbacks)
    this.modal.dataset.productId = productId;
    this.modal.dataset.productTitle = productTitle;
    this.modal.dataset.productImage = imageToUse || productImage || '';

    // Handle variant selection
    if (variants && variants.length > 0) {
      // Find out-of-stock variants
      const outOfStockVariants = variants.filter(v => !v.available);

      if (outOfStockVariants.length === 0) {
        // All variants available - shouldn't happen, but handle gracefully
        console.warn('All variants are available for this product');
        return;
      }

      if (outOfStockVariants.length === 1 && variants.length === 1) {
        // Single out-of-stock variant - pre-select it and hide dropdown
        if (variantSelectWrapper) {
          variantSelectWrapper.style.display = 'none';
        }
        this.modal.dataset.selectedVariantId = outOfStockVariants[0].id;
        this.modal.dataset.selectedVariantTitle = cleanVariantTitle(outOfStockVariants[0].title || 'Default');
        if (outOfStockVariants[0].price != null) this.modal.dataset.selectedVariantPrice = outOfStockVariants[0].price;
      } else {
        // Multiple variants - show dropdown with only out-of-stock options
        if (variantSelectWrapper) {
          variantSelectWrapper.style.display = 'block';
        }
        
        if (variantSelect) {
          // Clear existing options
          variantSelect.innerHTML = '<option value="">Select size...</option>';
          
          // Add only out-of-stock variants
          outOfStockVariants.forEach(variant => {
            const option = document.createElement('option');
            option.value = variant.id;
            const cleanedTitle = cleanVariantTitle(variant.title || '');
            // If cleaned title is empty, show empty string (don't show "Default Title")
            option.textContent = cleanedTitle || '';
            variantSelect.appendChild(option);
          });

          // Assigning onchange (not addEventListener) replaces whatever handler a
          // previous open - or the PDP chooser, which uses the same slot - left on
          // this shared select. Stacked stale listeners were able to run after a
          // fresh pick and delete it (their own candidate list can't contain it).
          variantSelect.onchange = (e) => {
            const selectedVariant = outOfStockVariants.find(v => v.id == e.target.value);
            if (selectedVariant) {
              this.modal.dataset.selectedVariantId = selectedVariant.id;
              this.modal.dataset.selectedVariantTitle = cleanVariantTitle(selectedVariant.title || '');
              if (selectedVariant.price != null) this.modal.dataset.selectedVariantPrice = selectedVariant.price;
            } else {
              // Back on the "Select size..." placeholder — nothing is chosen.
              delete this.modal.dataset.selectedVariantId;
              delete this.modal.dataset.selectedVariantTitle;
              delete this.modal.dataset.selectedVariantPrice;
            }
            // The submit gate reads selectedVariantId, but its own listeners only
            // cover the email field — without this, choosing a size after typing
            // the email leaves submit disabled.
            window.backInStockKlaviyoInstance?.refreshSubmitState();
          };
        }
      }
    }

    // Sync the submit gate with this open's state (a fresh open never has a
    // size chosen unless the single sold-out variant branch pre-selected it).
    window.backInStockKlaviyoInstance?.refreshSubmitState();

    // Show modal
    this.modal.setAttribute('open', '');
    document.body.style.overflow = 'hidden';
  }
}

// Wait for custom elements to be defined (e.g., product-form)
function waitForCustomElements() {
  return new Promise((resolve) => {
    if (customElements.get('product-form')) {
      // Custom element already defined
      resolve();
    } else {
      // Wait for custom element to be defined
      customElements.whenDefined('product-form').then(() => {
        resolve();
      });
      // Fallback timeout
      setTimeout(() => resolve(), 1000);
    }
  });
}

// Initialize when DOM is ready
async function initializeBIS() {
  // Exposed so CollectionBackInStock can re-run refreshSubmitState — its size
  // dropdown lives outside this instance's own listeners.
  window.backInStockKlaviyoInstance = new BackInStockKlaviyo();
  
  // Wait for custom elements to be defined before initializing button handlers
  await waitForCustomElements();
  
  window.collectionBackInStockInstance = new CollectionBackInStock();
  
  // Retry initialization after delays to catch dynamically loaded buttons
  // This is especially important for product pages with sections that load asynchronously
  const retryDelays = [500, 1000, 2000, 3000];
  retryDelays.forEach((delay, index) => {
    setTimeout(() => {
      // Re-initialize CollectionBackInStock to catch any buttons that were added
      const existingInstance = window.collectionBackInStockInstance;
      if (existingInstance && existingInstance.reinit) {
        existingInstance.reinit();
      } else {
        window.collectionBackInStockInstance = new CollectionBackInStock();
      }
    }, delay);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeBIS);
} else {
  initializeBIS();
}
